import { ArrowRight, Search, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { GsImage, XatsPriceValue } from '@/components/help-center/latest-power-shared';
import { PowerNavIcon } from '@/components/help-center/MaskedSvgIcon';
import PowerDarkCardShell from '@/components/help-center/powers/PowerDarkCardShell';
import { PainelDashedAddCardButton } from '@/components/painel/cms/SmilieCodeField';
import { painelInputClass } from '@/components/painel/cms/painel-form';
import { useFairtradePrices } from '@/components/painel/powers/powers-cms-context';
import { applyFairtradePrices } from '@/lib/fairtrade-data';
import { getPowerCategoryBadgeStyle } from '@/lib/power-category-theme';
import {
  findByNameOrSlug,
  RELATED_EXPLORE_MAX_ITEMS,
  type RelatedExploreConfig,
} from '@/lib/related-explore';
import {
  formatFairtradePrice,
  getPowerCategoryLabel,
  type PowerInfo,
} from '@/lib/powers-data';
import { cn } from '@/lib/utils';

type CatalogItem = {
  slug: string;
  name: string;
  category?: string;
  description?: { short?: string; long?: string };
  prices?: {
    store?: string;
    fairtrade_min?: number;
    fairtrade_max?: number;
  };
};

type RelatedExploreEditorFieldProps = {
  value: RelatedExploreConfig;
  onChange: (next: RelatedExploreConfig) => void;
  catalog: CatalogItem[];
  defaultTitle: string;
  itemLabel?: string;
  helpText?: string;
  currentSlug?: string;
  /** Grelha de 4 cards estilo portal + busca (editor de powers). */
  cardLayout?: boolean;
};

function toPreviewPower(item: CatalogItem): PowerInfo {
  return {
    id: 0,
    name: item.name,
    slug: item.slug,
    description: {
      short: item.description?.short ?? '',
      long: item.description?.long ?? '',
    },
    prices: {
      store: item.prices?.store ?? '0',
      fairtrade_min: item.prices?.fairtrade_min ?? 0,
      fairtrade_max: item.prices?.fairtrade_max ?? 0,
    },
    status: false,
    released: '',
    category: item.category ?? 'smiley',
    type: item.category ?? 'smiley',
    limited: false,
    smilies: [],
    pawns: [],
  };
}

const PLACEHOLDER_RELATED_POWER: PowerInfo = {
  id: 0,
  name: 'Nome do power',
  slug: 'power',
  description: {
    short: 'Digite o nome do power para pré-visualizar o card.',
    long: '',
  },
  prices: {
    store: '0',
    fairtrade_min: 500,
    fairtrade_max: 700,
  },
  status: false,
  released: '',
  category: 'smiley',
  type: 'smiley',
  limited: false,
  smilies: [],
  pawns: [],
};

function padRelatedSlots(items: string[]): string[] {
  return Array.from({ length: RELATED_EXPLORE_MAX_ITEMS }, (_, i) => items[i] ?? '');
}

/** Pré-visualização do card «Explore mais» da página pública (sem link). */
function ExploreRelatedCardPreview({ power }: { power: PowerInfo }) {
  const categoryBadgeStyle = getPowerCategoryBadgeStyle(power.category);

  return (
    <PowerDarkCardShell
      className="h-full"
      innerClassName="flex items-center gap-3 p-3 sm:gap-4 sm:p-4"
    >
      <div
        className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl"
        style={{
          background:
            'radial-gradient(circle at 50% 48%, #112268 0%, #0a1450 42%, #060c38 75%, #030719 100%)',
          boxShadow: '0 0 0 1.5px #0b1658, 0 0 0 2.5px #0f1e70',
        }}
      >
        <GsImage
          code={power.slug}
          className="flex h-full w-full items-center justify-center"
          imgClassName="max-h-[44px] max-w-[44px] object-contain"
        />
      </div>

      <div className="min-w-0 flex-1">
        <div className="mb-0.5 flex items-center gap-2">
          <h4 className="truncate font-poppins text-sm font-bold text-white">{power.name}</h4>
          <span
            className="hidden shrink-0 items-center rounded-full border px-2 py-0.5 font-poppins text-[9px] font-bold uppercase tracking-wide sm:inline-flex"
            style={categoryBadgeStyle}
          >
            {getPowerCategoryLabel(power.category)}
          </span>
        </div>
        <p className="line-clamp-1 truncate font-inter text-[11px] leading-snug text-blue-100/70">
          {power.description.short || 'Sem descrição curta.'}
        </p>
        <XatsPriceValue className="mt-0.5 text-[11px] text-white">
          {formatFairtradePrice(power.prices.fairtrade_min, power.prices.fairtrade_max)}
        </XatsPriceValue>
      </div>

      <ArrowRight className="h-4 w-4 shrink-0 text-blue-100/30" />
    </PowerDarkCardShell>
  );
}

/** Card do portal com desfoque + caixa de busca compacta (padrão do hero «Novo power»). */
function ExploreRelatedCardSlot({
  query,
  onQueryChange,
  onClear,
  preview,
  isSelf,
}: {
  query: string;
  onQueryChange: (next: string) => void;
  onClear: () => void;
  preview: PowerInfo | undefined;
  isSelf: boolean;
}) {
  const unlocked = Boolean(preview && !isSelf);
  const cardPower = unlocked && preview ? preview : PLACEHOLDER_RELATED_POWER;

  return (
    <div className="relative overflow-hidden rounded-2xl">
      {unlocked ? (
        <div className="absolute right-1.5 top-1.5 z-40">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 rounded-lg border border-white/10 bg-[#030023]/85 text-rose-300 backdrop-blur-md hover:bg-rose-500/15 hover:text-rose-100"
            onClick={onClear}
            aria-label="Limpar power"
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      ) : null}

      <div className={cn(!unlocked && 'pointer-events-none select-none')}>
        <ExploreRelatedCardPreview power={cardPower} />
      </div>

      {!unlocked ? (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-[#030023]/70 px-2 backdrop-blur-[3px]">
          <div className="w-[min(100%,11.5rem)] space-y-1.5 rounded-xl border border-white/20 bg-[#030023] px-2.5 py-2 shadow-2xl shadow-black/50 ring-1 ring-sky-500/25">
            <div className="text-center">
              <p className="font-inter text-[8px] font-bold uppercase tracking-[0.16em] text-sky-300/70">
                Power
              </p>
              <h3 className="font-poppins text-[11px] font-bold leading-tight text-white">
                Nome do power
              </h3>
            </div>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 z-10 h-3 w-3 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => onQueryChange(e.target.value)}
                placeholder="Ex.: Astrox…"
                autoComplete="off"
                spellCheck={false}
                className={cn(
                  'h-7 w-full rounded-lg border border-white/20 bg-[#12103a] pl-7 pr-2',
                  'font-poppins text-[11px] text-white caret-white outline-none',
                  'placeholder:text-slate-500',
                  'focus:border-sky-400/50 focus:ring-1 focus:ring-sky-400/40',
                  isSelf && 'border-amber-400/50'
                )}
              />
            </div>
            {isSelf ? (
              <p className="text-center font-inter text-[9px] leading-tight text-amber-200/90">
                Item actual — ignorado no site.
              </p>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function RelatedExploreEditorField({
  value,
  onChange,
  catalog,
  defaultTitle,
  itemLabel = 'Nome ou slug',
  helpText = 'Os cards usam imagem, descrição e preço do item indicado. Se a lista estiver vazia, a página pública preenche automaticamente.',
  currentSlug,
  cardLayout = false,
}: RelatedExploreEditorFieldProps) {
  const { prices: fairtradePrices } = useFairtradePrices();
  const classicRows = value.items.length > 0 ? value.items : [''];
  const cardSlots = padRelatedSlots(value.items);

  const resolvePreview = (query: string): PowerInfo | undefined => {
    const found = findByNameOrSlug(catalog, query);
    if (!found) return undefined;
    const base = toPreviewPower(found);
    return fairtradePrices ? applyFairtradePrices(base, fairtradePrices) : base;
  };

  const setCardSlot = (index: number, raw: string) => {
    const next = padRelatedSlots(value.items);
    next[index] = raw;
    onChange({ ...value, items: next });
  };

  const clearCardSlot = (index: number) => {
    const next = padRelatedSlots(value.items);
    next[index] = '';
    onChange({ ...value, items: next });
  };

  const setClassicItemAt = (index: number, raw: string) => {
    const next = [...classicRows];
    next[index] = raw;
    onChange({ ...value, items: next.slice(0, RELATED_EXPLORE_MAX_ITEMS) });
  };

  const addClassicItem = () => {
    const filled = value.items.map((item) => item.trim()).filter(Boolean);
    if (filled.length >= RELATED_EXPLORE_MAX_ITEMS) return;
    if (value.items.length > filled.length) return;
    onChange({ ...value, items: [...filled, ''] });
  };

  const removeClassicItem = (index: number) => {
    const next = classicRows.filter((_, i) => i !== index);
    onChange({ ...value, items: next.length === 1 && !next[0]?.trim() ? [] : next });
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="font-poppins text-slate-300">Título da secção</Label>
        <Input
          value={value.title ?? ''}
          onChange={(e) => onChange({ ...value, title: e.target.value })}
          className={painelInputClass}
          placeholder={defaultTitle}
        />
      </div>

      <div className="space-y-2">
        <Label className="font-poppins text-slate-300">
          Itens ({RELATED_EXPLORE_MAX_ITEMS} máx.)
        </Label>

        {cardLayout ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {cardSlots.map((item, index) => {
              const trimmed = item.trim();
              const preview = trimmed ? resolvePreview(trimmed) : undefined;
              const found = trimmed ? findByNameOrSlug(catalog, trimmed) : undefined;
              const isSelf = Boolean(
                found &&
                  currentSlug &&
                  found.slug.toLowerCase() === currentSlug.trim().toLowerCase()
              );

              return (
                <ExploreRelatedCardSlot
                  key={index}
                  query={item}
                  onQueryChange={(next) => setCardSlot(index, next)}
                  onClear={() => clearCardSlot(index)}
                  preview={preview}
                  isSelf={isSelf}
                />
              );
            })}
          </div>
        ) : (
          <div className="space-y-2">
            {classicRows.map((item, index) => {
              const trimmed = item.trim();
              const found = trimmed ? findByNameOrSlug(catalog, trimmed) : undefined;
              const isSelf = Boolean(
                found &&
                  currentSlug &&
                  found.slug.toLowerCase() === currentSlug.trim().toLowerCase()
              );
              const unknown = Boolean(trimmed) && !found;

              return (
                <div key={index} className="space-y-1">
                  <div className="flex gap-2">
                    <Input
                      value={item}
                      onChange={(e) => setClassicItemAt(index, e.target.value)}
                      className={cn(
                        painelInputClass,
                        unknown || isSelf ? 'border-amber-400/40' : undefined
                      )}
                      placeholder={`${itemLabel} ${index + 1}`}
                      autoComplete="off"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="h-10 w-10 shrink-0 rounded-xl border-white/15 p-0 text-rose-300 hover:bg-rose-500/10"
                      onClick={() => removeClassicItem(index)}
                      disabled={classicRows.length <= 1 && !trimmed}
                      aria-label="Remover item"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  {isSelf ? (
                    <p className="font-inter text-xs text-amber-300/90">
                      Este é o item actual — será ignorado na página pública.
                    </p>
                  ) : null}
                  {unknown ? (
                    <p className="font-inter text-xs text-amber-300/90">
                      Não encontrado no catálogo. Pode guardar na mesma; o card só aparece se o
                      nome/slug existir.
                    </p>
                  ) : null}
                  {found && !isSelf ? (
                    <p className="font-inter text-xs text-emerald-400/80">
                      {found.name} ({found.slug})
                    </p>
                  ) : null}
                </div>
              );
            })}

            {classicRows.length < RELATED_EXPLORE_MAX_ITEMS ? (
              <PainelDashedAddCardButton
                label="Adicionar item"
                onClick={addClassicItem}
                icon={<PowerNavIcon className="h-6 w-6" />}
              />
            ) : null}
          </div>
        )}
      </div>

      {helpText ? <p className="font-inter text-xs text-slate-500">{helpText}</p> : null}
    </div>
  );
}
